import { getExecutingEnvironment } from "@app/resolveModule/env";
import type { SessionStatePayload } from "@ext/agent/components/types/chat";
import type { SelectedModel } from "@ext/agent/llm";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import { keepLatestSessions, withSessionsQuotaEviction } from "./sessionsStorage";

const STORAGE_VERSION = 1;

const hasUserMessage = (session: SessionStatePayload): boolean =>
	Array.isArray(session.events) && session.events.some((event) => event.type === "user_message");

const getSessionCreatedAt = (session: SessionStatePayload): number => session.events?.[0]?.ts ?? 0;

const getLatestUsedModel = (sessions: SessionStatePayload[], activeSessionId?: string): SelectedModel | undefined => {
	let latest: SessionStatePayload | undefined;

	for (const session of sessions) {
		if (session.id === activeSessionId || !session.lastUsedModel) continue;
		if (!latest || getSessionCreatedAt(session) > getSessionCreatedAt(latest)) latest = session;
	}

	return latest?.lastUsedModel;
};

interface AgentStoreState {
	activeSessionId: string | null;
	sessions: SessionStatePayload[];
	sessionsOwnerId: string | null;
	/** The most recent explicit model pick, regardless of session. */
	lastSelectedModel: SelectedModel | null;
	/** Unsent model picks by session id. */
	pendingModelsBySessionId: Record<string, SelectedModel>;
	setActiveSessionId: (id: string) => void;
	clearActiveSessionId: () => void;
	setSessions: (sessions: SessionStatePayload[]) => void;
	upsertSession: (session: SessionStatePayload) => void;
	setSessionSelectedModel: (id: string, model: SelectedModel) => void;
	markSessionModelUsed: (id: string, model: SelectedModel) => void;
	setSessionBrowserAllowed: (id: string, allowed: boolean) => void;
	removeSession: (id: string) => void;
	dropSessionsOfOtherOwner: (ownerId: string) => void;
}

const useAgentStore = create<AgentStoreState>()(
	persist(
		(set) => ({
			activeSessionId: null,
			sessions: [],
			sessionsOwnerId: null,
			lastSelectedModel: null,
			pendingModelsBySessionId: {},
			setActiveSessionId: (id) =>
				set((state) => (state.activeSessionId === id ? state : { activeSessionId: id })),
			clearActiveSessionId: () =>
				set((state) => (state.activeSessionId === null ? state : { activeSessionId: null })),
			setSessions: (sessions) => set({ sessions }),
			upsertSession: (session) =>
				set((state) => ({
					sessions: [...state.sessions.filter((s) => s.id !== session.id), session],
				})),
			setSessionSelectedModel: (id, model) =>
				set((state) => ({
					lastSelectedModel: model,
					pendingModelsBySessionId: { ...state.pendingModelsBySessionId, [id]: model },
				})),
			markSessionModelUsed: (id, model) =>
				set((state) => {
					const { [id]: _usedModel, ...pendingModelsBySessionId } = state.pendingModelsBySessionId;
					return {
						sessions: state.sessions.map((s) => (s.id === id ? { ...s, lastUsedModel: model } : s)),
						pendingModelsBySessionId,
					};
				}),
			setSessionBrowserAllowed: (id, allowed) =>
				set((state) => ({
					sessions: state.sessions.map((s) => (s.id === id ? { ...s, browserAllowed: allowed } : s)),
				})),
			removeSession: (id) =>
				set((state) => {
					const { [id]: _removedModel, ...pendingModelsBySessionId } = state.pendingModelsBySessionId;
					return { sessions: state.sessions.filter((s) => s.id !== id), pendingModelsBySessionId };
				}),
			dropSessionsOfOtherOwner: (ownerId) =>
				set((state) =>
					state.sessionsOwnerId === ownerId
						? state
						: {
								sessionsOwnerId: ownerId,
								sessions: [],
								activeSessionId: null,
								lastSelectedModel: null,
								pendingModelsBySessionId: {},
							},
				),
		}),
		{
			name: "agent-state",
			storage: createJSONStorage(() => withSessionsQuotaEviction(localStorage)),
			partialize: (s) =>
				getExecutingEnvironment() === "next"
					? {
							activeSessionId: s.activeSessionId,
							sessions: keepLatestSessions(s.sessions, s.activeSessionId),
							sessionsOwnerId: s.sessionsOwnerId,
						}
					: { activeSessionId: s.activeSessionId },
			version: STORAGE_VERSION,
			migrate: (persistedState) => {
				const { apiKey, selectedModelId, ...state } = persistedState as {
					apiKey?: string;
					selectedModelId?: string | null;
					activeSessionId?: string | null;
				};
				return state;
			},
		},
	),
);

export const useStoredSessions = () => useAgentStore((s) => s.sessions);
export const useActiveSessionId = () => useAgentStore((s) => s.activeSessionId);
export const useActiveSessionUsage = () =>
	useAgentStore((s) => s.sessions.find((sess) => sess.id === s.activeSessionId)?.usage);
export const useActiveSessionBrowser = () =>
	useAgentStore((s) => s.sessions.find((sess) => sess.id === s.activeSessionId)?.browser);
export const useActiveSessionBrowserAllowed = () =>
	useAgentStore((s) => !!s.sessions.find((sess) => sess.id === s.activeSessionId)?.browserAllowed);
export const useActiveSessionSelectedModel = () =>
	useAgentStore((s) => {
		if (!s.activeSessionId) return undefined;
		const activeSession = s.sessions.find((sess) => sess.id === s.activeSessionId);
		if (activeSession && !hasUserMessage(activeSession))
			return s.lastSelectedModel ?? getLatestUsedModel(s.sessions, s.activeSessionId);
		return s.pendingModelsBySessionId[s.activeSessionId] ?? activeSession?.lastUsedModel;
	});
export const useActiveSessionLastUsedModel = () =>
	useAgentStore((s) => s.sessions.find((sess) => sess.id === s.activeSessionId)?.lastUsedModel);

export const getActiveSessionId = () => useAgentStore.getState().activeSessionId;
export const getSessions = () => useAgentStore.getState().sessions;

export const setActiveSessionId = (id: string) => useAgentStore.getState().setActiveSessionId(id);
export const clearActiveSessionId = () => useAgentStore.getState().clearActiveSessionId();
export const setSessions = (sessions: SessionStatePayload[]) => useAgentStore.getState().setSessions(sessions);
export const upsertSession = (session: SessionStatePayload) => useAgentStore.getState().upsertSession(session);
export const setSessionSelectedModel = (id: string, model: SelectedModel) =>
	useAgentStore.getState().setSessionSelectedModel(id, model);
export const markSessionModelUsed = (id: string, model: SelectedModel) =>
	useAgentStore.getState().markSessionModelUsed(id, model);
export const setSessionBrowserAllowed = (id: string, allowed: boolean) =>
	useAgentStore.getState().setSessionBrowserAllowed(id, allowed);
export const removeSession = (id: string) => useAgentStore.getState().removeSession(id);
export const dropSessionsOfOtherOwner = (ownerId: string) => useAgentStore.getState().dropSessionsOfOtherOwner(ownerId);
